import { toast } from 'react-toastify';
import { all, call, put, takeLatest, select } from '@redux-saga/core/effects';

import {
  loadingWidgetAllSettings,
  setPrevSettings,
  setWidgetAllSettings,
  setAutoMessages,
  setSelectedAutoMessage,
  setSelectedAutoMessageErrors,
  setOfficeHoursSettings,
  setEmailSettings,
  setGeneralSettings,
  setAutoMessagesLoading,
  setWidgetIsSubmitting,
  resetSelectedMsgStateToInitial,
  // response delay
  getResponseDelayDataSuccess,
  getResponseDelayDataFailure,
  putResponseDelayDataSuccess,
  putResponseDelayDataFailure,
  postResponseDelayDataSuccess,
  postResponseDelayDataFailure,
  deleteResponseDelayDataSuccess,
  deleteResponseDelayDataFailure,
  // form generator
  postFormGeneratorDataSuccess,
  postFormGeneratorDataFailure,
  // responsive robot
  getResponsiveRobotDataSuccess,
  getResponsiveRobotDataFailure,
  deleteResponsiveRobotDataSuccess,
  deleteResponsiveRobotDataFailure,
  putResponsiveRobotDataSuccess,
  putResponsiveRobotDataFailure,
  setAiBotStatus,
} from './settings.actions';
import {
  handlePostWidgetSettings,
  handleFetchWidgetSettings,
  handleSaveEmailSettings,
  handleSaveGeneralSettings,
  handleUpdateAutoMessage,
  handleUpdateOfficeHoursSettings,
  handleUploadSiteLogo,
  handleGetChannelInfo,
  handleGetWidgetAndAutoMessageSettings,
  handleDeleteAutoMessage,
  handleAddNewAutoMessage,
  // response delay
  handleGetResponseDelayData,
  handlePutResponseDelayData,
  handlePostResponseDelayData,
  handleDeleteResponseDelayData,
  // form generator
  handlePostFormGeneratorData,
  // responsive robot
  handleGetResponsiveRobotData,
  handleDeleteResponsiveRobotData,
  handlePutResponsiveRobotData,
  handlePostResponsiveRobotData,
  handleChangeAiBotStatus,
} from './settings.helper';
import {
  createStorableState,
  createAutoMessagesObjects,
  createApiAutoMessageObject,
  createOfficeHoursSettingsObject,
  getApiReadyOfficeHoursSettings,
  createEmailSettingsObject,
  getEmailSettingsApiPayload,
  createGeneralSettingsObject,
  getApiReadyGeneralSettings,
  getToggleStatusNotif,
} from './settings.adapter';
import {
  handleErrors,
  responseDelayHandleError,
  getUniqueId,
  getNotifMsg,
} from 'utils';
import {
  getNewMsgPreDefinedTitle,
  checkHasError,
  getMessageError,
  getInitialAutoMessage,
} from './settings.utils';

import Types from './settings.types';

const getSettings = ({ settingsData, userData, appData }) => ({
  landing: settingsData.widget?.data?.landing,
  customizations: settingsData.widget?.data?.customizations,
  display: settingsData.widget?.data?.view,
  positions: settingsData.widget?.data?.positions,
  isSettingsFetching: settingsData.widget?.isLoading,
  prevData: settingsData.widget?.prevData,
  currentData: settingsData.widget?.data,
  autoMessages: settingsData.autoMessages.data,
  profile: userData.profile,
  locale: appData.locale,
});

const getSelectedWorkspace = ({ websitesData }) =>
  websitesData.selectedWorkspace;

export function* fetchWidgetSettings({ payload }) {
  yield put(loadingWidgetAllSettings(true));
  const { locale } = yield select(getSettings);

  try {
    const settings = yield select(getSettings);
    const returnedData = yield handleFetchWidgetSettings(payload);
    if (returnedData)
      yield put(
        setWidgetAllSettings(createStorableState(returnedData.data, settings))
      );
    yield put(loadingWidgetAllSettings(false));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* postWidgetSettings({ payload }) {
  yield put(setWidgetIsSubmitting(true));
  const { locale } = yield select(getSettings);

  try {
    yield handlePostWidgetSettings(payload.data);
    yield put(setPrevSettings(payload.prevData));

    toast.success(getNotifMsg('SETTINGS_SUCCESSFUL_SAVED', locale));
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    yield put(setWidgetIsSubmitting(false));
  }
}

export function* onFetchWidgetSettings() {
  yield takeLatest(Types.FETCH_WIDGET_SETTINGS_START, fetchWidgetSettings);
}

export function* onPostWidgetSettings() {
  yield takeLatest(Types.POST_WIDGET_SETTINGS_START, postWidgetSettings);
}

export function* fetchAllAutoMessages({ payload }) {
  const { locale } = yield select(getSettings);

  try {
    const channelId = payload;
    yield put(setAutoMessagesLoading(true));
    const widgetAndAutoMessageSettings =
      yield handleGetWidgetAndAutoMessageSettings(channelId);
    const autoMessageSettings = widgetAndAutoMessageSettings.data?.triggers;

    const autoMessages = createAutoMessagesObjects(autoMessageSettings);

    yield put(setAutoMessages(autoMessages));
  } catch (error) {
    handleErrors(error, locale);
  }
}

function* fetchAiChatBotSettings({ payload }) {
  const { locale } = yield select(getSettings);

  try {
    const channelId = payload;
    const settings = yield handleGetWidgetAndAutoMessageSettings(channelId);
    const chatbotSettings = settings.data?.chatBot;

    // because of inconsistency in backend api data structured the options are destructed in this way.
    // settings can be boolean (old  settings before adding connect with operator option) or object (new settings)
    const { isEnabled: status, connectBTN: connectAgent } =
      typeof chatbotSettings === 'object'
        ? chatbotSettings
        : { isEnabled: chatbotSettings, connectBTN: true };

    yield put(setAiBotStatus({ status, connectAgent }));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* fetchOfficeHoursSettings({ payload }) {
  const { locale } = yield select(getSettings);

  try {
    const channelId = payload;
    const channelInfoResData = yield handleGetChannelInfo(channelId);
    const channelInfo = channelInfoResData.data?.channel;
    const officeSettings = channelInfo.meta.officeHours || [];
    const officeHoursSettings = createOfficeHoursSettingsObject(
      officeSettings,
      channelInfo.meta.notOfficeHours
    );
    yield put(
      setOfficeHoursSettings({ ...officeHoursSettings, setPrevData: true })
    );
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* fetchEmailsSettings({ payload }) {
  const { locale } = yield select(getSettings);

  try {
    const channelId = payload;
    const channelInfoResData = yield handleGetChannelInfo(channelId);
    const channelInfo = channelInfoResData.data?.channel;
    const emailSettings = createEmailSettingsObject(
      channelInfo.meta.emailSetting
    );
    yield put(setEmailSettings({ ...emailSettings, setPrevData: true }));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* fetchGeneralSettings({ payload }) {
  const { locale } = yield select(getSettings);
  try {
    const channelId = payload;
    const channelInfoResData = yield handleGetChannelInfo(channelId);
    const settingsInfo = channelInfoResData.data?.setting;
    const channelInfo = channelInfoResData.data?.channel;
    const generalSettings = createGeneralSettingsObject(
      {
        ...channelInfo.meta,
        ...settingsInfo.meta,
      },
      locale
    );
    yield put(setGeneralSettings({ ...generalSettings, setPrevData: true }));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* toggleAutoMessageStatus({ payload }) {
  const { setLoadingToggle, updatedMessage, channelId } = payload;
  const { autoMessages, profile, locale } = yield select(getSettings);

  const STATUS_TEXT_LOCALE = {
    fa: {
      enabled: 'فعال',
      disabled: 'غیرفعال',
    },
    en: {
      enabled: 'enabled',
      disabled: 'disabled',
    },
  };

  try {
    const payload = createApiAutoMessageObject(updatedMessage);
    const res = yield handleUpdateAutoMessage(channelId, payload);

    const newStatusText =
      updatedMessage.status === 'active'
        ? STATUS_TEXT_LOCALE[locale].enabled
        : STATUS_TEXT_LOCALE[locale].disabled;

    if (res.success) {
      const updatedMessages = autoMessages.map((item) => {
        if (item.id === updatedMessage.id) {
          return { ...updatedMessage, updatedBy: profile.userId };
        }
        return item;
      });
      yield put(setAutoMessages(updatedMessages));
      toast.success(getToggleStatusNotif(newStatusText, locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoadingToggle(false);
  }
}

export function* publishAutoMessage({ payload }) {
  const { data, setLoading } = payload;
  const { history, message, channelId } = data;
  const { locale } = yield select(getSettings);

  try {
    const errors = getMessageError(message, locale);

    yield put(setSelectedAutoMessageErrors(errors));
    if (checkHasError(errors)) {
      toast.error(getNotifMsg('CORRECT_ERRORS', locale));
      return;
    }
    const apiPayload = createApiAutoMessageObject(message);
    const res = yield handleAddNewAutoMessage({
      ...apiPayload,
      channelId,
      enabled: true,
    });

    if (res.success) {
      toast.success(getNotifMsg('AUTO_MSG_ADDED', locale));
      yield put(resetSelectedMsgStateToInitial());
      history.push(`/settings/auto-message`);
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoading(false);
  }
}

export function* deleteAutoMessage({ payload }) {
  const { messageId, channelId, setLoadingDelete, setConfirmModalVisible } =
    payload;
  const { locale } = yield select(getSettings);
  try {
    const res = yield handleDeleteAutoMessage(channelId, {
      triggerId: messageId,
    });

    if (res.success) {
      const updatedMessages = createAutoMessagesObjects(res.data.triggers);
      yield put(setAutoMessages(updatedMessages));
      toast.success(getNotifMsg('AUTO_MSG_DELETED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoadingDelete(false);
    setConfirmModalVisible(false);
  }
}

export function* determineSelectedAutoMessage({ payload }) {
  const { locale } = yield select(getSettings);
  try {
    const { messageId, channelId, autoMessages } = payload;
    const message = autoMessages?.find((item) => item.id === String(messageId));

    if (messageId === 'new' || !message) {
      yield put(
        setSelectedAutoMessage(
          {
            ...getInitialAutoMessage(locale),
            title: getNewMsgPreDefinedTitle(autoMessages, locale),
            id: getUniqueId(),
            channelId,
            mode: 'new',
          },
          true
        )
      );
      return;
    }

    yield put(
      setSelectedAutoMessage({ ...message, channelId, mode: 'edit' }, true)
    );
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* saveOfficeHoursSettings({ payload }) {
  const { setLoading, settings, channelId } = payload;
  const { locale } = yield select(getSettings);

  try {
    const apiPayload = getApiReadyOfficeHoursSettings(settings, channelId);
    const resData = yield handleUpdateOfficeHoursSettings(apiPayload);
    if (resData.success) {
      yield put(setOfficeHoursSettings({ ...settings, setPrevData: true }));
      toast.success(getNotifMsg('CHANGES_SAVED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoading(false);
  }
}

export function* saveEmailSettings({ payload }) {
  const { setLoadingSubmit, ...settings } = payload;
  const { locale } = yield select(getSettings);

  try {
    const apiPayload = getEmailSettingsApiPayload(settings);
    const response = yield handleSaveEmailSettings(apiPayload);

    if (response.success) {
      yield put(setEmailSettings({ ...settings, setPrevData: true }));
      toast.success(getNotifMsg('CHANGES_SAVED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoadingSubmit(false);
  }
}

export function* saveGeneralSettings({ payload }) {
  const { setLoading, settings } = payload;
  const { locale } = yield select(getSettings);

  try {
    let toUploadPhoto = settings.logo.url;

    //upload logo
    if (settings.logo.file) {
      try {
        const response = yield handleUploadSiteLogo(settings.logo.file);
        toUploadPhoto = response.filePath;
      } catch (error) {}
    }

    const apiPayload = getApiReadyGeneralSettings({
      ...settings,
      logo: toUploadPhoto,
    });
    const response = yield handleSaveGeneralSettings(apiPayload);

    if (response.success) {
      yield put(setGeneralSettings({ ...settings, setPrevData: true }));
      toast.success(getNotifMsg('CHANGES_SAVED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoading(false);
  }
}

export function* editAutoMessage({ payload }) {
  const { data, setLoading } = payload;
  const { history, channelId, message } = data;
  const { locale } = yield select(getSettings);

  try {
    const errors = getMessageError(message);

    yield put(setSelectedAutoMessageErrors(errors));
    if (checkHasError(errors)) {
      toast.error(getNotifMsg('CORRECT_ERRORS', locale));
      return;
    }
    const apiPayload = createApiAutoMessageObject(message);
    const res = yield handleUpdateAutoMessage(channelId, apiPayload);

    if (res.success) {
      toast.success(getNotifMsg('SUCCESSFULL_EDITING', locale));
      yield put(resetSelectedMsgStateToInitial());
      history.push(`/settings/auto-message`);
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoading(false);
  }
}

export function* changeMessageSender({ payload }) {
  const { autoMessages, profile, locale } = yield select(getSettings);
  const { data, setLoading } = payload;
  const { channelId, updatedMessage } = data;

  try {
    const payload = createApiAutoMessageObject(updatedMessage);
    const res = yield handleUpdateAutoMessage(channelId, payload);

    if (res.success) {
      const updatedMessages = autoMessages.map((item) => {
        if (item.id === updatedMessage.id) {
          return { ...updatedMessage, updatedBy: profile.userId };
        }
        return item;
      });
      yield put(setAutoMessages(updatedMessages));
      toast.success(getNotifMsg('AUTO_MSG_SENDER_CHANGES', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoading(false);
  }
}

// RESPONSE DELAY
export function* getResponseDelayDataSaga({ payload }) {
  const { locale } = yield select(getSettings);
  const { channelId } = payload;
  try {
    const response = yield handleGetResponseDelayData(channelId);
    yield put(getResponseDelayDataSuccess(response));
  } catch (error) {
    yield put(getResponseDelayDataFailure(error));
    responseDelayHandleError(error, locale);
  }
}

export function* putResponseDelayDataSaga({ payload }) {
  const { locale } = yield select(getSettings);
  const { channelId, data } = payload;
  try {
    const response = yield handlePutResponseDelayData(channelId, data);
    yield put(putResponseDelayDataSuccess(response));
    toast.success(getNotifMsg('RESPONSE_DELAY_CONDITION', locale));
    yield put(handleGetResponseDelayData(channelId));
  } catch (error) {
    yield put(putResponseDelayDataFailure(error));
    responseDelayHandleError(error, locale);
  }
}

export function* postResponseDelayDataSaga(action) {
  const { locale } = yield select(getSettings);
  try {
    const response = yield call(handlePostResponseDelayData, action.payload);
    yield put(postResponseDelayDataSuccess(response));
    toast.success(getNotifMsg('RESPONSE_DELAY_FORM_SUBMIT', locale));
  } catch (error) {
    yield put(postResponseDelayDataFailure(error));
    responseDelayHandleError(error, locale);
  }
}

export function* deleteResponseDelayDataSaga({ payload }) {
  const { locale } = yield select(getSettings);
  const { channelId, index } = payload;
  try {
    const response = yield handleDeleteResponseDelayData(channelId, index);
    yield put(deleteResponseDelayDataSuccess(response));
    toast.success(getNotifMsg('RESPONSE_DELAY_CONDITION_DELETE', locale));
    yield put(handleGetResponseDelayData(channelId));
  } catch (error) {
    yield put(deleteResponseDelayDataFailure(error));
    handleErrors(error, locale);
  }
}

// FORM GENERATOR
export function* postFormGeneratorDataSaga(action) {
  const { locale } = yield select(getSettings);
  try {
    const response = yield call(handlePostFormGeneratorData, action.payload);
    yield put(postFormGeneratorDataSuccess(response));
    toast.success(getNotifMsg('FORM_GENERATOR_SUBMIT', locale));
  } catch (error) {
    yield put(postFormGeneratorDataFailure(error));
    handleErrors(error, locale);
  }
}

// RESPONSIVE ROBOT
export function* getResponsiveRobotDataSaga({ payload }) {
  const { locale } = yield select(getSettings);
  const { channelId } = payload;
  try {
    const response = yield handleGetResponsiveRobotData(channelId);
    yield put(getResponsiveRobotDataSuccess(response));
  } catch (error) {
    yield put(getResponsiveRobotDataFailure(error));
    handleErrors(error, locale);
  }
}

export function* deleteResponsiveRobotDataSaga({ payload }) {
  const { locale } = yield select(getSettings);
  const { channelId, teamId } = payload;
  try {
    const response = yield handleDeleteResponsiveRobotData(channelId, teamId);
    yield put(deleteResponsiveRobotDataSuccess(response));
    toast.success(getNotifMsg('RESPONSIVE_ROBOT_OPERATION_DELETE', locale));
    yield put(handleGetResponsiveRobotData(channelId));
  } catch (error) {
    yield put(deleteResponsiveRobotDataFailure(error));
    handleErrors(error, locale);
  }
}

export function* putResponsiveRobotDataSaga({ payload }) {
  const { locale } = yield select(getSettings);
  const { channelId, data } = payload;
  try {
    const response = yield handlePutResponsiveRobotData(channelId, data);
    yield put(putResponsiveRobotDataSuccess(response));
    toast.success(getNotifMsg('RESPONSIVE_ROBOT_OPERATION_UPDATE', locale));
    yield put(handleGetResponsiveRobotData(channelId));
  } catch (error) {
    yield put(putResponsiveRobotDataFailure(error));
    handleErrors(error, locale);
  }
}

export function* postResponsiveRobotDataSaga(action) {
  const { locale } = yield select(getSettings);
  const { data, setLoadingSubmit } = action.payload;
  try {
    const response = yield call(handlePostResponsiveRobotData, data);
    if (response.success) {
      toast.success(getNotifMsg('RESPONSIVE_ROBOT_SUBMIT', locale));

      yield call(getResponsiveRobotDataSaga, {
        payload: { channelId: data.channelId },
      });
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoadingSubmit(false);
  }
}

export function* onGetResponsiveRobotData() {
  yield takeLatest(Types.RESPONSIVE_ROBOT_GET_DATA, getResponsiveRobotDataSaga);
}

export function* onDeleteResponsiveRobotData() {
  yield takeLatest(
    Types.RESPONSIVE_ROBOT_DELETE_DATA,
    deleteResponsiveRobotDataSaga
  );
}

export function* onPutResponsiveRobotData() {
  yield takeLatest(Types.RESPONSIVE_ROBOT_PUT_DATA, putResponsiveRobotDataSaga);
}

function* changeChatBotEnabledStatus({ payload }) {
  const { locale } = select(getSettings);

  const { status, connectAgent, setLoadingSwitch } = payload;
  try {
    const selectedWs = yield select(getSelectedWorkspace);

    const payloadData = {
      channelId: selectedWs?.id,
      isEnabled: status,
      connectBTN: connectAgent,
    };

    const response = yield handleChangeAiBotStatus(payloadData);

    if (response.success) {
      yield put(setAiBotStatus({ status, connectAgent }));
      toast.success('تغییرات اعمال شد');
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoadingSwitch(null);
  }
}

function* onChangeChatBotEnabledStatus() {
  yield takeLatest(
    Types.CHANGE_AI_BOT_ENABLED_STATUS,
    changeChatBotEnabledStatus
  );
}

function* onFetchAiBotSettings() {
  yield takeLatest(Types.FETCH_AI_BOT_SETTINGS, fetchAiChatBotSettings);
}

export function* onPostResponsiveRobotData() {
  yield takeLatest(
    Types.RESPONSIVE_ROBOT_POST_DATA,
    postResponsiveRobotDataSaga
  );
}

export function* onPostFormGeneratorData() {
  yield takeLatest(Types.FORM_GENERATOR_POST_DATA, postFormGeneratorDataSaga);
}

export function* onGetResponseDelayData() {
  yield takeLatest(Types.GET_RESPONSE_DELAY_DATA, getResponseDelayDataSaga);
}

export function* onPutResponseDelayData() {
  yield takeLatest(Types.PUT_RESPONSE_DELAY_DATA, putResponseDelayDataSaga);
}

export function* onPostResponseDelayData() {
  yield takeLatest(Types.POST_RESPONSE_DELAY_DATA, postResponseDelayDataSaga);
}

export function* onDeleteResponseDelayData() {
  yield takeLatest(
    Types.DELETE_RESPONSE_DELAY_DATA,
    deleteResponseDelayDataSaga
  );
}

export function* onFetchAllAutoMessages() {
  yield takeLatest(Types.FETCH_AUTO_MESSAGES, fetchAllAutoMessages);
}

export function* onPublishAutoMessage() {
  yield takeLatest(Types.PUBLISH_SELECTED_AUTO_MESSAGE, publishAutoMessage);
}

export function* onToggleAutoMessageStatus() {
  yield takeLatest(Types.TOGGLE_AUTO_MESSAGE_STATUS, toggleAutoMessageStatus);
}

export function* onDeleteAutoMessage() {
  yield takeLatest(Types.DELETE_AUTO_MESSAGE, deleteAutoMessage);
}

export function* onDetermineSelectedAutoMessage() {
  yield takeLatest(
    Types.DETERMINE_SELECTED_AUTO_MESSAGE,
    determineSelectedAutoMessage
  );
}

export function* onSaveOfficeHoursSettings() {
  yield takeLatest(Types.SAVE_OFFICE_HOURS_SETTINGS, saveOfficeHoursSettings);
}

export function* onSaveEmailSettings() {
  yield takeLatest(Types.SAVE_EMAIL_SETTINGS, saveEmailSettings);
}

export function* onSaveGeneralSettings() {
  yield takeLatest(Types.SAVE_GENERAL_SETTINGS, saveGeneralSettings);
}

export function* onEditAutoMessage() {
  yield takeLatest(Types.EDIT_AUTO_MESSAGE, editAutoMessage);
}

export function* onFetchOfficeHoursSettings() {
  yield takeLatest(Types.FETCH_OFFICE_HOURS_SETTINGS, fetchOfficeHoursSettings);
}

export function* onFetchEmailsSettings() {
  yield takeLatest(Types.FETCH_EMAILS_SETTINGS, fetchEmailsSettings);
}

export function* onFetchGeneralSettings() {
  yield takeLatest(Types.FETCH_GENERAL_SETTINGS, fetchGeneralSettings);
}

export function* onChangeMessageSender() {
  yield takeLatest(Types.CHANGE_MESSAGE_SENDER, changeMessageSender);
}

export default function* settingsSaga() {
  yield all([
    call(onFetchWidgetSettings),
    call(onPostWidgetSettings),
    call(onFetchAllAutoMessages),
    call(onPublishAutoMessage),
    call(onToggleAutoMessageStatus),
    call(onDeleteAutoMessage),
    call(onDetermineSelectedAutoMessage),
    call(onSaveOfficeHoursSettings),
    call(onSaveEmailSettings),
    call(onSaveGeneralSettings),
    call(onEditAutoMessage),
    call(onFetchOfficeHoursSettings),
    call(onFetchEmailsSettings),
    call(onFetchGeneralSettings),
    call(onChangeMessageSender),
    call(onGetResponseDelayData),
    call(onPutResponseDelayData),
    call(onPostResponseDelayData),
    call(onDeleteResponseDelayData),
    call(onPostFormGeneratorData),
    call(onGetResponsiveRobotData),
    call(onDeleteResponsiveRobotData),
    call(onPutResponsiveRobotData),
    call(onPostResponsiveRobotData),
    call(onChangeChatBotEnabledStatus),
    call(onFetchAiBotSettings),
  ]);
}
